import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Heart, User, Menu, X, Globe } from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const languages = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
];

const Navbar = () => {
  const { t, language, setLanguage, user, logout, getCartCount, favorites } = useApp();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLanguageChange = (code) => {
    const lang = languages.find(l => l.code === code);
    document.documentElement.dir = lang?.code === 'ar' ? 'rtl' : 'ltr';
    setLanguage(code);
  };

  const cartCount = getCartCount();
  const currentLang = languages.find(l => l.code === language);

  return (
    <header className="sticky top-0 z-50 glass-strong">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/home" 
            className="font-heading text-xl md:text-2xl"
            data-testid="navbar-logo"
          >
            <span className="zuiver-letters-nav">
              {'Zuiver'.split('').map((letter, index) => (
                <span key={index} className="zuiver-letter-nav" style={{ animationDelay: `${index * 0.42}s` }}>
                  {letter}
                </span>
              ))}
            </span>
            <span className="text-white">Parfum</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/products/men" 
              className="text-white hover:text-[#D4AF37] transition-colors uppercase tracking-wider text-sm font-medium"
              data-testid="nav-men"
            >
              {t('men')}
            </Link>
            <Link 
              to="/products/women" 
              className="text-white hover:text-[#D4AF37] transition-colors uppercase tracking-wider text-sm font-medium"
              data-testid="nav-women"
            >
              {t('women')}
            </Link>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="p-2 text-white hover:text-[#D4AF37] transition-colors flex items-center gap-1"
                  data-testid="language-dropdown"
                >
                  <span className="text-lg">{currentLang?.flag}</span>
                  <span className="hidden md:inline text-sm uppercase">{language}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1C1917] border-[#2C1B18]">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className="text-white hover:text-[#D4AF37] hover:bg-[#2C1B18] cursor-pointer"
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Favorites */}
            <Link 
              to={user ? "/account/favorites" : "/login"}
              className="relative p-2 text-white hover:text-[#D4AF37] transition-colors"
              data-testid="nav-favorites"
            >
              <Heart size={22} />
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {favorites.length}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link 
              to="/cart"
              className="relative p-2 text-white hover:text-[#D4AF37] transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingBag size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Account */}
            <Link 
              to={user ? "/account" : "/login"}
              className="p-2 text-white hover:text-[#D4AF37] transition-colors"
              data-testid="nav-account"
            >
              <User size={22} />
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass-strong border-t border-[#2C1B18]">
          <nav className="container mx-auto px-4 py-4 space-y-4">
            <Link 
              to="/products/men" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-white hover:text-[#D4AF37] transition-colors uppercase tracking-wider text-sm font-medium py-2"
            >
              {t('perfume_for_men')}
            </Link>
            <Link 
              to="/products/women" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-white hover:text-[#D4AF37] transition-colors uppercase tracking-wider text-sm font-medium py-2"
            >
              {t('perfume_for_women')}
            </Link>
            <div className="border-t border-[#2C1B18] pt-4">
              {user ? (
                <>
                  <Link 
                    to="/account" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-white hover:text-[#D4AF37] py-2"
                  >
                    {t('my_account')}
                  </Link>
                  <button 
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="block text-red-500 hover:text-red-400 py-2"
                  >
                    {t('logout')}
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-white hover:text-[#D4AF37] py-2"
                >
                  {t('login')} / {t('register')}
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
